import { Ancillary, AncillaryOpaqueValue } from "@b2bportal/purchase-api";
import { createSelector } from "@reduxjs/toolkit";
import {
  ProductCfarQuote,
  CfarQuoteResultEnum,
  CfarQuoteEnum,
  HotelCfarQuote,
  HotelCfarQuoteAdditionalInfo,
} from "redmond";
import { DO_NOT_APPLY_OPTION_KEY } from "..";
import {
  AVAILABLE,
  HOTELS_CFAR,
  HOTEL_CFAR_REFUND_DISPLAY,
  HOTEL_CFAR_REFUND_DISPLAY_REFUND_DOLLAR_AMOUNT_RR,
  HOTEL_CFAR_REFUND_DISPLAY_REFUND_DOLLAR_AMOUNT_ADDITIONAL_PLACES,
  HOTELS_CFAR_ELIGIBLE_POLICY,
  FINTECH_HOTEL_UX_UPDATED,
  HOTEL_CFAR_REFUND_DISPLAY_NON_REF_PARTIAL,
  HOTEL_CFAR_MODEL_V1,
  CFAR_MODEL_RANDOM_PERCENT_16_26,
  CFAR_MODEL_ADVANCE_STAR_RATING_23_28,
  CFAR_MODEL_ADVANCE_DAYS_10_21,
  CFAR_MODEL_ADVANCE_STAR_RATING_23_28_COMPARISON,
  CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_COPY,
  CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_NEW_UX,
  CFAR_MODEL_RANDOM_PERCENT_5_30_FR_ELIGIBLE,
  CFAR_MODEL_STAR_AND_GBV_15_28_FR_ELIGIBLE_NEW_UX,
  HBUX_LODGING_ERROR_COPY_UPDATE,
} from "../../../../context/experiments";
import { IStoreState } from "../../../../reducers/types";

export const ancillaryExperimentsSelector = (state: IStoreState) =>
  state.hotelAncillary.experiments;

export const ancillaryStateSelector = (state: IStoreState) =>
  state.hotelAncillary;

export const hotelCfarVariantSelector = createSelector(
  ancillaryExperimentsSelector,
  (experiments) => experiments?.[HOTELS_CFAR]
);

export const lodgingErrorCopyUpdateVariantSelector = createSelector(
  ancillaryExperimentsSelector,
  (experiments) => experiments?.[HBUX_LODGING_ERROR_COPY_UPDATE]
);

export const isLodgingErrorCopyUpdateEnabledSelector = createSelector(
  lodgingErrorCopyUpdateVariantSelector,
  (variant) => variant === AVAILABLE
);

export const isHotelCfarEnabledSelector = createSelector(
  hotelCfarVariantSelector,
  (hotelCfarVariant) => hotelCfarVariant === AVAILABLE
);

export const hotelCfarRefundDisplayVariantSelector = createSelector(
  ancillaryExperimentsSelector,
  (experiments) => experiments?.[HOTEL_CFAR_REFUND_DISPLAY]
);

export const isHotelCfarRefundDisplayShowAmountEnabledSelector = createSelector(
  hotelCfarRefundDisplayVariantSelector,
  (hotelCfarRefundDisplayVariant) =>
    hotelCfarRefundDisplayVariant ===
    HOTEL_CFAR_REFUND_DISPLAY_REFUND_DOLLAR_AMOUNT_RR
);

export const isHotelCfarRefundDisplayShowAmountAdditionalPlacesEnabledSelector =
  createSelector(
    hotelCfarRefundDisplayVariantSelector,
    (hotelCfarRefundDisplayVariant) =>
      hotelCfarRefundDisplayVariant ===
      HOTEL_CFAR_REFUND_DISPLAY_REFUND_DOLLAR_AMOUNT_ADDITIONAL_PLACES
  );

export const isHotelCfarRefundDisplayNonRefPartialEnabledSelector =
  createSelector(
    hotelCfarRefundDisplayVariantSelector,
    (hotelCfarRefundExperimentVariant) =>
      hotelCfarRefundExperimentVariant ===
      HOTEL_CFAR_REFUND_DISPLAY_NON_REF_PARTIAL
  );

export const hotelCfarEligiblePolicyVariantSelector = createSelector(
  ancillaryExperimentsSelector,
  (experiments) => experiments?.[HOTELS_CFAR_ELIGIBLE_POLICY]
);

export const fintechHotelUpdatedUxVariantSelector = createSelector(
  ancillaryExperimentsSelector,
  (experiments) => experiments?.[FINTECH_HOTEL_UX_UPDATED]
);

export const isFintechHotelUpdatedUxEnabledSelector = createSelector(
  fintechHotelUpdatedUxVariantSelector,
  (fintechHotelUpdatedUxVariant) => fintechHotelUpdatedUxVariant === AVAILABLE
);

export const isCustomizePageEnabledSelector = createSelector(
  isHotelCfarEnabledSelector,
  (isHotelCfarEnabled) => isHotelCfarEnabled
);

export const lodgingIdWithCfarQuotesSelector = (state: IStoreState) =>
  state.hotelAncillary.lodgingId;

export const roomInfoCfarQuotesSelector = (state: IStoreState) =>
  state.hotelAncillary.roomInfoCfarQuotes;

export const fetchCfarQuotesCallStateSelector = (state: IStoreState) =>
  state.hotelAncillary.fetchCfarQuoteCallState;

export const selectedCfarIdSelector = (state: IStoreState) =>
  state.hotelAncillary.selectedCfarId;

export const selectedHtsCfarOfferIdSelector = (state: IStoreState) =>
  state.hotelAncillary.selectedHtsCfarOfferId;

export const hasSelectedRefundableRoomSelector = (state: IStoreState) =>
  state.hotelAncillary.hasSelectedRefundableRoom;

export const hasCfarAttached = createSelector(
  selectedCfarIdSelector,
  (selectedCfarId): boolean =>
    !!selectedCfarId && selectedCfarId.value !== DO_NOT_APPLY_OPTION_KEY
);

export const getAllAncillaries = createSelector(
  selectedCfarIdSelector,
  (selectedCfarId): AncillaryOpaqueValue[] => {
    const ancillaries: AncillaryOpaqueValue[] = [];
    if (selectedCfarId && selectedCfarId.value !== DO_NOT_APPLY_OPTION_KEY) {
      ancillaries.push({
        type: Ancillary.CFAR,
        value: selectedCfarId,
      });
    }

    return ancillaries;
  }
);

// TODO - CFAR: add the selector that returns the CFAR offer by the room id
/*
  note: this selector is an intermediate solution until the room id gets added into the response;
  for example, use the following states from the shop/reducer to work with this selector:
    1) chosenRoomInfoIndex - getHotelShopChosenRoomInfoIndex
    2) chosenProductIndex - getHotelShopChosenProductIndex
*/
const getHotelCfarProductByRoomIdSelector = createSelector(
  roomInfoCfarQuotesSelector,
  (
      roomInfoCfarQuotes
    ): ((props: {
      selectedRoomId?: string;
      selectedRateId?: string;
    }) => ProductCfarQuote | undefined) =>
    ({ selectedRoomId, selectedRateId }) => {
      const roomInfoCfarQuotesByRoomId = roomInfoCfarQuotes.find(
        (roomInfoCfarQuote) =>
          roomInfoCfarQuote.roomInfo.roomId === selectedRoomId
      );

      return roomInfoCfarQuotesByRoomId?.cfarQuotes.find((quote) => {
        if (quote.CfarQuote === CfarQuoteEnum.HotelCfarEligible) {
          return selectedRateId === quote.rateId?.value;
        }
        return false;
      });
    }
);

export const getHotelCfarQuoteByRoomIdSelector = createSelector(
  getHotelCfarProductByRoomIdSelector,
  (
      getHotelCfarProductByRoomId
    ): ((props: {
      selectedRoomId?: string;
      selectedRateId?: string;
    }) => HotelCfarQuote | null) =>
    ({ selectedRoomId, selectedRateId }) => {
      const cfarProduct = getHotelCfarProductByRoomId({
        selectedRoomId,
        selectedRateId,
      });
      if (cfarProduct?.CfarQuote === CfarQuoteEnum.HotelCfarEligible) {
        if (
          cfarProduct.cfarQuoteResult.CfarQuoteResult ===
          CfarQuoteResultEnum.HotelCfarQuote
        ) {
          return cfarProduct.cfarQuoteResult;
        }
      }
      return null;
    }
);

export const getHotelCfarQuoteAdditionalInfoByRoomIdSelector = createSelector(
  getHotelCfarProductByRoomIdSelector,
  (
      getHotelCfarProductByRoomId
    ): ((props: {
      selectedRoomId?: string;
      selectedRateId?: string;
    }) => HotelCfarQuoteAdditionalInfo | null) =>
    ({ selectedRoomId, selectedRateId }) => {
      const cfarProduct = getHotelCfarProductByRoomId({
        selectedRoomId,
        selectedRateId,
      });
      if (cfarProduct?.CfarQuote === CfarQuoteEnum.HotelCfarEligible) {
        if (
          cfarProduct.cfarQuoteResult.CfarQuoteResult ===
          CfarQuoteResultEnum.HotelCfarQuote
        ) {
          return cfarProduct.additionalInfo;
        }
      }
      return null;
    }
);

export const hotelCfarModelV1VariantSelector = createSelector(
  ancillaryExperimentsSelector,
  (experiment) => experiment?.[HOTEL_CFAR_MODEL_V1]
);

// we are checking if the user is enrolled in the new model pricing variants
// by checking !Any(legacy_variants).
// previosuly we were checking Any(new_variants), but that implementation
// was too restricitive with the new addition of variants that were
// falling under the new pricing model.
export const isHotelCfarModelV1EnabledSelector = createSelector(
  hotelCfarModelV1VariantSelector,
  (variant) => {
    if (!variant) {
      return false;
    }
    if (
      // these are the legacy (old) variants
      variant.variantName == CFAR_MODEL_ADVANCE_DAYS_10_21 ||
      variant.variantName == CFAR_MODEL_RANDOM_PERCENT_16_26 ||
      variant.variantName == CFAR_MODEL_ADVANCE_STAR_RATING_23_28 ||
      variant.variantName == CFAR_MODEL_ADVANCE_STAR_RATING_23_28_COMPARISON
    ) {
      return false;
    }
    return true;
  }
);

export const isHotelCfarModelV1RefundableRoomUpdatedCopySelector =
  createSelector(
    hotelCfarModelV1VariantSelector,
    (variant) =>
      variant?.variantName ===
      CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_COPY
  );

export const isHotelCfarModelV1RefundableRoomUpdatedUXSelector = createSelector(
  hotelCfarModelV1VariantSelector,
  (variant) => {
    if (variant?.variantName) {
      return [
        CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_NEW_UX,
        CFAR_MODEL_RANDOM_PERCENT_5_30_FR_ELIGIBLE,
        CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_COPY,
        CFAR_MODEL_STAR_AND_GBV_15_28_FR_ELIGIBLE_NEW_UX,
      ].includes(variant.variantName);
    }
    return false;
  }
);

export const getHasAddedCfarFromModalSelector = createSelector(
  ancillaryStateSelector,
  (ancillaryState) => ancillaryState.hasAddedCfarFromModal ?? false
);
